import { HttpClient } from "common/services/http-client.service";
import { ApiResult } from "common/models/api-service/api-result.model";
import { withApiResponse } from "common/utils/rtk-query.util";
import { chatbotConfig } from "../config/chatbot.config";
import { ENV_CONFIG } from "common/config/env.config";
import { msalInstance } from "common/config/auth.config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export interface ChatPayload {
  query?: string;
  user_query?: string;
  document_type?: string;
  flow_id?: number | string;
  file_name?: string;
  [key: string]: any;
}

export interface ChatResponse {
  response: string;
  sources?: any[];
  [key: string]: any;
}

export interface FlowConfiguration {
  flow_id: number;
  flow_name: string;
  config_id: number;
  [key: string]: any;
}

/**
 * Service for AI Chatbot operations
 */
export class ChatbotService extends HttpClient {
  /**
   * Send a chat message to the AI chatbot
   * @param payload - Chat payload containing query and optional metadata
   * @returns Chat response from the AI
   */
  static async sendChatMessage(payload: ChatPayload): Promise<ChatResponse> {
    try {
      // Choose endpoint based on chat mode
      const preferredBaseUrl =
        ENV_CONFIG.CHATBOT_FUNCAPP_URL ||
        (payload?.mode === "document"
          ? ENV_CONFIG.GDX_FUNCAPP_API_URL
          : ENV_CONFIG.GDX_API_URL);

      const baseUrl = preferredBaseUrl?.replace(/\/$/, "");
      if (!baseUrl) {
        throw new Error("Chat service URL is not configured");
      }

      const chatEndpoint = `${baseUrl}/api/chat`;

      // Get authentication token
      const request = {
        scopes: [process.env.REACT_APP_API_SCOPE || ""]
      };

      let tokenResult;
      try {
        tokenResult = await msalInstance.acquireTokenSilent(request);
      } catch (error: any) {
        if (error instanceof InteractionRequiredAuthError) {
          tokenResult = await msalInstance.acquireTokenPopup(request);
        } else {
          throw error;
        }
      }

      // Make authenticated fetch call to function app
      const response = await fetch(chatEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenResult.accessToken}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error sending chat message:", error);
      throw error;
    }
  }

  /**
   * Fetch flows by document type
   * @param documentType - The document type to fetch flows for
   * @returns List of flow configurations
   */
  static fetchFlowsByDocumentType(documentType: string) {
    const config = chatbotConfig.fetchFlowsByDocumentType(documentType);
    return withApiResponse<FlowConfiguration[]>(async () => {
      const response = await this.invokeApi<FlowConfiguration[]>(config);
      return new ApiResult<FlowConfiguration[]>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }

  /**
   * Fetch document list by flow ID with pagination
   * @param flowId - The flow ID
   * @param page - Page number
   * @param pageSize - Number of items per page
   * @returns Paginated document list
   */
  public static fetchDocumentsByFlow(
    flowId: number | string,
    page?: number,
    pageSize?: number
  ) {
    const config = chatbotConfig.fetchDocumentsByFlow(flowId, page, pageSize);
    return withApiResponse<any>(async () => {
      const response = await this.invokeApi<any>(config);
      return new ApiResult<any>(
        response.value,
        response.statusCode,
        ...response.errors
      );
    });
  }
}

// Export singleton instance
export const chatbotService = new ChatbotService();
